import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { LoadingSpinner, Drawer, AgGridTable } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { FaSyncAlt, FaListUl, FaFileExcel } from 'react-icons/fa';
import { MdBarChart } from 'react-icons/md';
import ProjectStatusRenderer from 'components/app/crm/project/component/ProjectStatusRenderer';
import {
  getProjectSummary,
  getProjectSummaryByYear,
  getContractSummary,
  getUnderShopDrawing,
  getNewJob,
  getWaitingRADToUnderShop,
  getUnderShopToUnderProduction,
  getFinishedShipmentJob,
} from 'redux/crm/report/masterScheduleSlice';
import PropTypes from 'prop-types';
import { fmtDate, fmtNum, fmtWeight } from 'helpers/utils';
import { splitRollupRow, summaryTotals } from './masterSummaryTotals';

const aggregateByField = (rows, keyField, weightField) => {
  const map = {};
  rows.forEach((r) => {
    const key = r[keyField] || 'Unknown';
    if (!map[key]) map[key] = { name: key, number: 0, weight: 0 };
    map[key].number += 1;
    map[key].weight += Number(r[weightField] || 0);
  });
  return Object.values(map).sort((a, b) => b.number - a.number);
};

// ── AG Grid column definitions ────────────────────────────────────────────────

const NUM_COL = { width: 110, type: 'numericColumn', cellStyle: { textAlign: 'right' } };

const BUILDING_COL_DEFS = [
  { headerName: 'J#',          field: 'j_number',         width: 110, pinned: 'left', filter: 'agTextColumnFilter' },
  { headerName: 'Building',    field: 'building_name',    flex: 1, minWidth: 90,  filter: 'agTextColumnFilter' },
  { headerName: 'Project',     field: 'project_name',     flex: 2, minWidth: 140, filter: 'agTextColumnFilter' },
  { headerName: 'Customer',    field: 'customer_name',    flex: 2, minWidth: 120, filter: 'agTextColumnFilter' },
  { headerName: 'Unit',        field: 'sales_unit_name',  width: 120, filter: 'agSetColumnFilter' },
  { headerName: 'Status',      field: 'status_name',      width: 150, filter: 'agSetColumnFilter', cellRenderer: (p) => <ProjectStatusRenderer value={p.value ?? '—'} data={{}} /> },
  { headerName: 'Entry Date',  field: 'job_entry_date',   width: 120, filter: 'agDateColumnFilter', valueFormatter: (p) => fmtDate(p.value) },
  { headerName: 'Wt (MT)',     field: 'est_final_weight', ...NUM_COL, valueFormatter: (p) => fmtNum(p.value) },
];

const BACKLOG_COL_DEFS = [
  { headerName: 'J#',          field: 'j_number',         width: 110, pinned: 'left', filter: 'agTextColumnFilter' },
  { headerName: 'Building',    field: 'building_name',    flex: 1, minWidth: 90,  filter: 'agTextColumnFilter' },
  { headerName: 'Project',     field: 'project_name',     flex: 2, minWidth: 140, filter: 'agTextColumnFilter' },
  { headerName: 'Customer',    field: 'customer_name',    flex: 2, minWidth: 120, filter: 'agTextColumnFilter' },
  { headerName: 'Unit',        field: 'sales_unit_name',  width: 120, filter: 'agSetColumnFilter' },
  { headerName: 'Status',      field: 'status_name',      width: 150, filter: 'agSetColumnFilter', cellRenderer: (p) => <ProjectStatusRenderer value={p.value ?? '—'} data={{}} /> },
  { headerName: 'Trans Date',  field: 'trans_date',       width: 120, filter: 'agDateColumnFilter', valueFormatter: (p) => fmtDate(p.value) },
  { headerName: 'Wt (MT)',     field: 'est_final_weight', ...NUM_COL, valueFormatter: (p) => fmtNum(p.value) },
];

const CONTRACT_COL_DEFS = [
  { headerName: 'J#',           field: 'j_number',        width: 110, pinned: 'left', filter: 'agTextColumnFilter' },
  { headerName: 'Project',      field: 'project_name',    flex: 2, minWidth: 140, filter: 'agTextColumnFilter' },
  { headerName: 'Customer',     field: 'customer_name',   flex: 2, minWidth: 120, filter: 'agTextColumnFilter' },
  { headerName: 'Type',         field: 'type_of_sales',   width: 100, filter: 'agSetColumnFilter' },
  { headerName: 'Unit',         field: 'sales_unit_name', width: 130, filter: 'agSetColumnFilter' },
  { headerName: 'Contract Date',field: 'contract_date',   width: 130, filter: 'agDateColumnFilter', valueFormatter: (p) => fmtDate(p.value) },
  { headerName: 'Value',        field: 'contract_value',  ...NUM_COL, valueFormatter: (p) => fmtNum(p.value) },
  { headerName: 'Wt (MT)',      field: 'contract_weight', ...NUM_COL, valueFormatter: (p) => fmtNum(p.value) },
];

// ── DetailDrawer ──────────────────────────────────────────────────────────────

const DetailDrawer = ({ open, title, rows, colDefs, weightKey, onClose }) => {
  const gridApiRef = useRef(null);

  const totalWeight = useMemo(
    () => rows.reduce((s, r) => s + Number(r[weightKey] || 0), 0),
    [rows, weightKey]
  );
  const pinnedBottom = useMemo(() => [{
    j_number:        'TOTAL',
    [weightKey]:     totalWeight,
    project_name:    '',
    customer_name:   '',
    building_name:   '',
    sales_unit_name: '',
    status_name:     '',
    contract_value:  rows.reduce((s, r) => s + Number(r.contract_value || 0), 0),
  }], [rows, weightKey, totalWeight]);

  const handleExport = useCallback(() => {
    gridApiRef.current?.exportDataAsExcel({ fileName: `${title}.xlsx` });
  }, [title]);

  return (
    <Drawer show={open} onHide={onClose} title={title} width="860px" minWidth="380px">
      {/* Summary bar */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-bg-slate-50 tw-border-b tw-border-slate-100 tw-text-xs tw-text-slate-500 tw-flex-shrink-0">
        <span>
          <span className="tw-font-semibold tw-text-slate-700">{rows.length.toLocaleString()}</span> records
          {' · '}Total weight: <span className="tw-font-semibold tw-text-slate-700">{fmtWeight(totalWeight)} M.T</span>
        </span>
        <button
          type="button"
          onClick={handleExport}
          disabled={rows.length === 0}
          className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-border tw-border-emerald-300 tw-rounded tw-bg-emerald-50 tw-text-emerald-700 hover:tw-bg-emerald-100 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-transition-colors tw-cursor-pointer"
        >
          <FaFileExcel size={12} />
          Export Excel
        </button>
      </div>

      {/* AG Grid */}
      <AgGridTable
        rowData={rows}
        columnDefs={colDefs}
        filter
        sort
        pagination
        pageSize={25}
        rowHeight={34}
        headerHeight={40}
        fontSize="12px"
        height="max(320px, calc(100dvh - 196px))"
        enableCellTextSelection
        pinnedBottomRowData={rows.length > 0 ? pinnedBottom : null}
        onGridReady={(params) => { gridApiRef.current = params.api; }}
      />
    </Drawer>
  );
};

DetailDrawer.propTypes = {
  open:      PropTypes.bool.isRequired,
  title:     PropTypes.string.isRequired,
  rows:      PropTypes.array.isRequired,
  colDefs:   PropTypes.array.isRequired,
  weightKey: PropTypes.string.isRequired,
  onClose:   PropTypes.func.isRequired,
};

// ── SummaryCard ───────────────────────────────────────────────────────────────

// `totalRow` is for the project-summary cards, whose first row is the API's own
// subtotal of the rows below it — see masterSummaryTotals.
const SummaryCard = ({ title, dateRange, rows, totalRow, accentColor, countLabel, onViewAll }) => {
  const { count: totalCount, weight: totalWeight } = summaryTotals(rows, totalRow);

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden tw-flex tw-flex-col">
      <div style={{ borderTop: `3px solid ${accentColor}` }} className="tw-px-4 tw-py-3 tw-border-b tw-border-slate-100">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
          <h3 className="tw-font-semibold tw-text-slate-800 tw-text-[13px] tw-leading-snug tw-mb-0 tw-flex-1">{title}</h3>
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0">
            <span className="tw-text-xs tw-font-bold tw-tabular-nums" style={{ color: accentColor }}>
              {totalCount.toLocaleString()} {countLabel || 'PROJS'}
            </span>
            <span className="tw-text-xs tw-text-slate-400 tw-tabular-nums">{fmtWeight(totalWeight)} M.T</span>
            {onViewAll && (
              <button
                type="button"
                onClick={onViewAll}
                title="View individual records"
                className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-sm tw-border tw-border-slate-200 tw-text-slate-500 hover:tw-text-slate-700 hover:tw-bg-slate-50 tw-text-[11px] tw-cursor-pointer tw-transition-colors"
              >
                <FaListUl size={9} /> View
              </button>
            )}
          </div>
        </div>
        {dateRange && <p className="tw-text-[11px] tw-text-slate-400 tw-mt-0.5 tw-mb-0">{dateRange}</p>}
      </div>

      <div className="tw-overflow-x-auto tw-flex-1">
        <table className="tw-w-full tw-text-xs">
          <thead>
            <tr className="tw-bg-slate-50 tw-border-b tw-border-slate-100">
              <th className="tw-px-4 tw-py-2 tw-text-left tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide">Status</th>
              <th className="tw-px-4 tw-py-2 tw-text-right tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide">Count</th>
              <th className="tw-px-4 tw-py-2 tw-text-right tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide">Weight (M.T)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? rows.map((row, i) => (
              <tr key={i} className="tw-border-t tw-border-slate-50 hover:tw-bg-slate-50/70 tw-transition-colors">
                <td className="tw-px-4 tw-py-2.5 tw-text-slate-700 tw-font-medium tw-leading-tight">{row.name}</td>
                <td className="tw-px-4 tw-py-2.5 tw-text-right tw-font-bold tw-tabular-nums" style={{ color: accentColor }}>
                  {Number(row.number).toLocaleString()}
                </td>
                <td className="tw-px-4 tw-py-2.5 tw-text-right tw-tabular-nums tw-text-slate-500">{fmtWeight(row.weight)}</td>
              </tr>
            )) : (
              <tr><td colSpan={3} className="tw-text-center tw-text-slate-300 tw-py-8 tw-italic">No data</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="tw-border-t-2 tw-border-slate-200 tw-bg-slate-50">
                <td className="tw-px-4 tw-py-2 tw-text-[11px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wide">Total</td>
                <td className="tw-px-4 tw-py-2 tw-text-right tw-font-bold tw-tabular-nums tw-text-slate-800">{totalCount.toLocaleString()}</td>
                <td className="tw-px-4 tw-py-2 tw-text-right tw-font-bold tw-tabular-nums tw-text-slate-800">{fmtWeight(totalWeight)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

SummaryCard.propTypes = {
  title:      PropTypes.string.isRequired,
  dateRange:  PropTypes.string,
  rows:       PropTypes.arrayOf(PropTypes.object).isRequired,
  totalRow:   PropTypes.object,
  accentColor:PropTypes.string.isRequired,
  countLabel: PropTypes.string,
  onViewAll:  PropTypes.func,
};

// ── SectionLabel ──────────────────────────────────────────────────────────────

const SectionLabel = ({ label, color }) => (
  <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2">
    <span
      className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-widest tw-px-3 tw-py-1 tw-rounded-xl tw-border tw-whitespace-nowrap"
      style={{ color, borderColor: `${color}50`, backgroundColor: `${color}12` }}
    >
      {label}
    </span>
    <div className="tw-flex-1 tw-h-px tw-bg-slate-100" />
  </div>
);
SectionLabel.propTypes = { label: PropTypes.string.isRequired, color: PropTypes.string.isRequired };

// ── Page ───────────────────────────────────────────────────────────────────────

const MasterSummaryPage = () => {
  const dispatch = useDispatch();
  const {
    projectSummary,
    projectSummaryByYear,
    contractSummary,
    underShopData,
    newJobData,
    waitingRADToUnderShopData,
    underShopToUnderProductionData,
    finishedShipmentData,
  } = useSelector((s) => s.crm_report_master_schedule);

  const [isLoading, setIsLoading] = useState(false);
  const [drawer,    setDrawer]    = useState({ open: false, title: '', rows: [], colDefs: BUILDING_COL_DEFS, weightKey: 'est_final_weight' });

  const currentYear = new Date().getFullYear();

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        dispatch(getProjectSummary({})),
        dispatch(getProjectSummaryByYear({ begin_date: `${currentYear}-01-01`, end_date: `${currentYear}-12-31` })),
        dispatch(getContractSummary({})),
        dispatch(getUnderShopDrawing({})),
        dispatch(getNewJob({})),
        dispatch(getWaitingRADToUnderShop({})),
        dispatch(getUnderShopToUnderProduction({})),
        dispatch(getFinishedShipmentJob({})),
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, currentYear]);

  useEffect(() => { load(); }, [load]);

  const closeDrawer = useCallback(() => setDrawer((d) => ({ ...d, open: false })), []);

  const openBuilding = useCallback((title, rows) => setDrawer({ open: true, title, rows, colDefs: BUILDING_COL_DEFS, weightKey: 'est_final_weight' }), []);
  const openBacklog  = useCallback((title, rows) => setDrawer({ open: true, title, rows, colDefs: BACKLOG_COL_DEFS,  weightKey: 'est_final_weight' }), []);
  const openContract = useCallback((title, rows) => setDrawer({ open: true, title, rows, colDefs: CONTRACT_COL_DEFS, weightKey: 'contract_weight'  }), []);

  // ── Aggregated rows ──────────────────────────────────────────────────────────
  const allSummary      = useMemo(() => splitRollupRow(projectSummary?.all),            [projectSummary]);
  const yearSummary     = useMemo(() => splitRollupRow(projectSummaryByYear),           [projectSummaryByYear]);
  const lastWeekSummary = useMemo(() => splitRollupRow(projectSummary?.last_week?.data), [projectSummary]);

  const contractThisRows = useMemo(() => aggregateByField(contractSummary?.this_week?.data || [], 'type_of_sales', 'contract_weight'), [contractSummary]);
  const contractLastRows = useMemo(() => aggregateByField(contractSummary?.last_week?.data || [], 'type_of_sales', 'contract_weight'), [contractSummary]);

  const enterShopRows  = useMemo(() => aggregateByField(underShopData || [], 'current_status', 'est_final_weight'), [underShopData]);
  const newJobThisRows = useMemo(() => aggregateByField(newJobData?.this_week?.data || [], 'current_status', 'est_final_weight'), [newJobData]);
  const newJobLastRows = useMemo(() => aggregateByField(newJobData?.last_week?.data || [], 'current_status', 'est_final_weight'), [newJobData]);

  const radThisRows  = useMemo(() => aggregateByField(waitingRADToUnderShopData?.this_week?.data   || [], 'status_name', 'est_final_weight'), [waitingRADToUnderShopData]);
  const radLastRows  = useMemo(() => aggregateByField(waitingRADToUnderShopData?.last_week?.data   || [], 'status_name', 'est_final_weight'), [waitingRADToUnderShopData]);
  const uspThisRows  = useMemo(() => aggregateByField(underShopToUnderProductionData?.this_week?.data || [], 'status_name', 'est_final_weight'), [underShopToUnderProductionData]);
  const uspLastRows  = useMemo(() => aggregateByField(underShopToUnderProductionData?.last_week?.data || [], 'status_name', 'est_final_weight'), [underShopToUnderProductionData]);
  const shipThisRows = useMemo(() => aggregateByField(finishedShipmentData?.this_week?.data || [], 'status_name', 'est_final_weight'), [finishedShipmentData]);
  const shipLastRows = useMemo(() => aggregateByField(finishedShipmentData?.last_week?.data || [], 'status_name', 'est_final_weight'), [finishedShipmentData]);

  const dr = (d) => d?.begin_date ? `${fmtDate(d.begin_date)} – ${fmtDate(d.end_date)}` : null;
  const n  = (arr) => (arr || []).length;

  return (
    <div className="tw-flex tw-flex-col tw-gap-5 tw-p-4">

      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-w-1 tw-h-10 tw-rounded-sm" style={{ background: 'linear-gradient(to bottom, #6366f1, #0ea5e9)' }} />
          <div className="tw-flex tw-items-center tw-gap-2">
            <MdBarChart className="tw-text-primary-500" size={22} />
            <div>
              <h1 className="tw-mb-0 tw-font-bold tw-text-xl tw-text-gray-900">Master Summary</h1>
              <p className="tw-text-gray-400 tw-text-xs tw-mb-0">
                Building count and weight by status — click <strong>View</strong> on any card to browse individual records
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-colors disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
        >
          <FaSyncAlt size={12} className={isLoading ? 'tw-animate-spin' : ''} />
          Reload
        </button>
      </div>

      {isLoading && <LoadingSpinner />}

      {!isLoading && (
        <>
          {/* Section 1 – Project Summary */}
          <SectionLabel label="Project Summary" color="#6366f1" />
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">
            <SummaryCard
              title={`PEBSTEEL PROJECTS ${currentYear}`}
              rows={yearSummary.rows}
              totalRow={yearSummary.totalRow}
              accentColor="#6366f1"
            />
            <SummaryCard
              title="PEBSTEEL ALL PROJECTS (up to date)"
              rows={allSummary.rows}
              totalRow={allSummary.totalRow}
              accentColor="#818cf8"
            />
            <SummaryCard
              title="PEBSTEEL PROJECTS (last week)"
              dateRange={projectSummary?.last_week?.date ? `Up to ${fmtDate(projectSummary.last_week.date)}` : null}
              rows={lastWeekSummary.rows}
              totalRow={lastWeekSummary.totalRow}
              accentColor="#a5b4fc"
            />
          </div>

          {/* Section 2 – Contract Summary */}
          <SectionLabel label="Contract Summary" color="#10b981" />
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <SummaryCard
              title="Contract summary (this week)"
              dateRange={dr(contractSummary?.this_week)}
              rows={contractThisRows}
              accentColor="#10b981"
              countLabel="PROJECTS"
              onViewAll={() => openContract(`Contract — this week  ·  ${n(contractSummary?.this_week?.data)} projects`, contractSummary?.this_week?.data || [])}
            />
            <SummaryCard
              title="Contract summary (last week)"
              dateRange={dr(contractSummary?.last_week)}
              rows={contractLastRows}
              accentColor="#34d399"
              countLabel="PROJECTS"
              onViewAll={() => openContract(`Contract — last week  ·  ${n(contractSummary?.last_week?.data)} projects`, contractSummary?.last_week?.data || [])}
            />
          </div>

          {/* Section 3 – Enter Shop + New Job */}
          <SectionLabel label="Shop Drawing & New Jobs" color="#f59e0b" />
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <SummaryCard
              title="Enter shop drawing (current)"
              rows={enterShopRows}
              accentColor="#f59e0b"
              countLabel="PROJECTS"
              onViewAll={() => openBuilding(`Enter shop drawing  ·  ${n(underShopData)} projects`, underShopData || [])}
            />
            <div className="tw-flex tw-flex-col tw-gap-4">
              <SummaryCard
                title="New job released (this week)"
                dateRange={dr(newJobData?.this_week)}
                rows={newJobThisRows}
                accentColor="#3b82f6"
                countLabel="PROJECTS"
                onViewAll={() => openBuilding(`New jobs — this week  ·  ${n(newJobData?.this_week?.data)} projects`, newJobData?.this_week?.data || [])}
              />
              <SummaryCard
                title="New job released (last week)"
                dateRange={dr(newJobData?.last_week)}
                rows={newJobLastRows}
                accentColor="#93c5fd"
                countLabel="PROJECTS"
                onViewAll={() => openBuilding(`New jobs — last week  ·  ${n(newJobData?.last_week?.data)} projects`, newJobData?.last_week?.data || [])}
              />
            </div>
          </div>

          {/* Section 4 – Waiting RAD → Under Shop */}
          <SectionLabel label="Waiting RAD → Under Shop Drawings" color="#8b5cf6" />
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <SummaryCard
              title="Job has waiting RAD to under Shop DWGS this week"
              dateRange={dr(waitingRADToUnderShopData?.this_week)}
              rows={radThisRows}
              accentColor="#8b5cf6"
              countLabel="PROJECTS"
              onViewAll={() => openBacklog(`Waiting RAD → Under Shop — this week  ·  ${n(waitingRADToUnderShopData?.this_week?.data)} projects`, waitingRADToUnderShopData?.this_week?.data || [])}
            />
            <SummaryCard
              title="Job has waiting RAD to under Shop DWGS last week"
              dateRange={dr(waitingRADToUnderShopData?.last_week)}
              rows={radLastRows}
              accentColor="#a78bfa"
              countLabel="PROJECTS"
              onViewAll={() => openBacklog(`Waiting RAD → Under Shop — last week  ·  ${n(waitingRADToUnderShopData?.last_week?.data)} projects`, waitingRADToUnderShopData?.last_week?.data || [])}
            />
          </div>

          {/* Section 5 – Under Shop → Under Production */}
          <SectionLabel label="Under Shop Drawings → Under Production" color="#0891b2" />
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <SummaryCard
              title="Job has under Shop to under Production this week"
              dateRange={dr(underShopToUnderProductionData?.this_week)}
              rows={uspThisRows}
              accentColor="#0891b2"
              countLabel="PROJECTS"
              onViewAll={() => openBacklog(`Under Shop → Production — this week  ·  ${n(underShopToUnderProductionData?.this_week?.data)} projects`, underShopToUnderProductionData?.this_week?.data || [])}
            />
            <SummaryCard
              title="Job has under Shop to under Production last week"
              dateRange={dr(underShopToUnderProductionData?.last_week)}
              rows={uspLastRows}
              accentColor="#22d3ee"
              countLabel="PROJECTS"
              onViewAll={() => openBacklog(`Under Shop → Production — last week  ·  ${n(underShopToUnderProductionData?.last_week?.data)} projects`, underShopToUnderProductionData?.last_week?.data || [])}
            />
          </div>

          {/* Section 6 – Finished Shipment */}
          <SectionLabel label="Finished Shipment" color="#ef4444" />
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <SummaryCard
              title="Finished shipment (this week)"
              dateRange={dr(finishedShipmentData?.this_week)}
              rows={shipThisRows}
              accentColor="#ef4444"
              countLabel="PROJECTS"
              onViewAll={() => openBacklog(`Finished shipment — this week  ·  ${n(finishedShipmentData?.this_week?.data)} projects`, finishedShipmentData?.this_week?.data || [])}
            />
            <SummaryCard
              title="Finished shipment (last week)"
              dateRange={dr(finishedShipmentData?.last_week)}
              rows={shipLastRows}
              accentColor="#f87171"
              countLabel="PROJECTS"
              onViewAll={() => openBacklog(`Finished shipment — last week  ·  ${n(finishedShipmentData?.last_week?.data)} projects`, finishedShipmentData?.last_week?.data || [])}
            />
          </div>
        </>
      )}

      <DetailDrawer
        open={drawer.open}
        title={drawer.title}
        rows={drawer.rows}
        colDefs={drawer.colDefs}
        weightKey={drawer.weightKey}
        onClose={closeDrawer}
      />
    </div>
  );
};

export default MasterSummaryPage;
